import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

import { REDSHIFT_AI_CONNECTOR_FUNCTION_NAME } from "./constants.ts";

const ACCOUNT_ID = /^\d{12}$/;

// copy of `region` in submodules/connector/services/p0-connector-redshift-ai/src/context.ts
const REGION = /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/;

// copy of `targetName` in submodules/connector/services/p0-connector-redshift-ai/src/context.ts
/** A provisioned cluster identifier or a Serverless workgroup name. */
const TARGET_NAME = /^[a-z][a-z0-9-]{0,63}$/;

// copy of USER_NAME in submodules/connector/services/p0-connector-redshift-ai/src/sql.ts
const USER_NAME = /^[A-Za-z0-9_][A-Za-z0-9_:+=,.@-]{0,126}$/;

// copy of IDENTIFIER in submodules/connector/services/p0-connector-redshift-ai/src/sql.ts
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$]{0,126}$/;

/** The parts of a `cluster` item id, `<account>/<region>/<name>`. */
export type RedshiftAiClusterId = {
  account: string;
  region: string;
  name: string;
};

/** The parts of a `cluster` item id, or `undefined` when it isn't one. */
export const parseClusterId = (id: string): RedshiftAiClusterId | undefined => {
  const [account, region, name, ...rest] = id.split("/");
  if (
    rest.length > 0 ||
    !account ||
    !region ||
    !name ||
    !ACCOUNT_ID.test(account) ||
    !REGION.test(region) ||
    !TARGET_NAME.test(name)
  )
    return undefined;
  return { account, region, name };
};

const targetType = {
  provisioned: Option({
    label: "Provisioned cluster",
    maturity: "beta",
    schema: {},
  }),
  serverless: Option({
    label: "Serverless workgroup",
    maturity: "beta",
    schema: {},
  }),
};

/**
 * One install, two rungs: `connector` (whose key is the `aws` item) →
 * `cluster`, which points at its connector with a `dynamic` element the
 * customer chooses.
 *
 * A chosen pointer's options come from `integrationOptionProvider`, which
 * hides any candidate whose key is also a key of the component being
 * installed, so each component's keys must not coincide with those of the
 * component it points at.
 */
export const RedshiftAiComponents = InstallSpec({
  // copy of `connector` in packages/integrations/databricks-ai/shared/src/components.ts
  connector: ItemComponent({
    description: `The ${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME} Lambda that P0 invokes to manage Redshift, one per AWS account. Its key is the ID of an AWS account installed in P0's AWS integration. Every cluster and workgroup in that account is reached through it.`,
    label: "Connector",
    maturity: "beta",
    schema: {
      region: Element({
        description: "The AWS region the connector's Lambda runs in",
        label: "Region",
        step: "new",
        type: "dynamic",
      }),
    },
    type: "dynamic",
  }),
  cluster: ItemComponent({
    description:
      "One Redshift provisioned cluster or Serverless workgroup. Its key is <AWS account ID>/<region>/<cluster identifier or workgroup name>.",
    label: "Cluster",
    maturity: "beta",
    // In the order the install form shows them.
    schema: {
      connector: Element({
        description: "The installed connector in this cluster's AWS account",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      type: Element({
        default: "provisioned",
        label: "Deployment type",
        options: targetType,
        step: "new",
        type: "select",
      }),
      database: Element({
        description:
          "The database the connector's admin statements connect to, such as dev. The connector finds the other databases from it.",
        label: "Database",
        type: "string",
        validator: async (_id, value) => {
          if (!IDENTIFIER.test(value))
            return "Use a database name made of letters, digits, '_' and '$', starting with a letter or '_'.";
        },
      }),
      objectOwner: Element({
        description:
          "The database user that takes over each table and view an agent created once its access ends. When empty, the connector's own user takes them over.",
        label: "Object owner",
        optional: true,
        type: "string",
        validator: async (_id, value) => {
          if (value && !USER_NAME.test(value))
            return "Use a Redshift user name, such as etl_owner or IAMR:etl-role.";
        },
      }),
      arn: Element({
        description:
          "The cluster's or workgroup's ARN, which the connector finds when the cluster is configured",
        label: "ARN",
        type: "generated",
      }),
      // Set by the backend each time the cluster is checked, never entered.
      warnings: Element({
        description:
          "What the last install check found that works but needs attention, one sentence each",
        hidden: true,
        label: "Warnings",
        optional: true,
        type: "hidden",
      }),
    },
    type: "string",
    validator: async (id) => {
      if (!parseClusterId(id))
        return "Use <AWS account ID>/<region>/<name>, such as 123456789012/us-west-2/analytics, where the name is the cluster identifier or workgroup name.";
    },
  }),
});
